import { motion } from 'framer-motion'
import { BleStatusIndicator } from './BleStatusIndicator'
import { useBle } from '@/features/ble/hooks/useBle'
import type { SensorPlacement } from '@/features/ble/types'

const PLACEMENT_OPTIONS: SensorPlacement[] = ['leftArm', 'rightArm', 'leftHand', 'rightHand']

function placementLabel(value: SensorPlacement): string {
  if (value === 'leftArm') return 'Left Arm'
  if (value === 'rightArm') return 'Right Arm'
  if (value === 'leftHand') return 'Left Hand'
  return 'Right Hand'
}

function getTakenPlacements(
  devices: Array<{ id: string; placement: SensorPlacement }>,
): Record<SensorPlacement, string | null> {
  return devices.reduce(
    (acc, device) => {
      acc[device.placement] = device.id
      return acc
    },
    {
      leftArm: null,
      rightArm: null,
      leftHand: null,
      rightHand: null,
    } as Record<SensorPlacement, string | null>,
  )
}

/**
 * ConnectionPanel — sidebar panel for BLE control.
 * Shows device info, connection status, and action buttons.
 */
export function ConnectionPanel() {
  const {
    status,
    error,
    reconnectAttempt,
    devices,
    connect,
    disconnectAll,
    disconnectDevice,
    setPlacement,
  } = useBle()
  const isBusy = status === 'scanning' || status === 'connecting' || status === 'reconnecting'
  const takenPlacements = getTakenPlacements(devices)

  return (
    <motion.aside
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex w-full flex-col gap-4 rounded-3xl border border-edge/60 bg-canvas/40 p-4 text-primary"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="eyebrow text-accent">Devices</span>
        <BleStatusIndicator status={status} />
      </div>

      {/* Device info */}
      {devices.length > 0 ? (
        <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
          {devices.map((managedDevice) => (
            <div
              key={managedDevice.id}
              className="rounded-2xl border border-accent/40 bg-inset/60 px-3 py-2.5"
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="truncate font-mono text-xs text-primary">{managedDevice.name}</p>
                <BleStatusIndicator status={managedDevice.status} showLabel={false} />
              </div>

              <select
                value={managedDevice.placement}
                onChange={(event) =>
                  setPlacement(managedDevice.id, event.target.value as SensorPlacement)
                }
                aria-label={`Placement for ${managedDevice.name}`}
                className="w-full rounded-lg border border-edge bg-canvas px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.1em] text-primary transition duration-200 hover:border-primary/40"
              >
                {PLACEMENT_OPTIONS.map((value) => (
                  <option
                    key={value}
                    value={value}
                    disabled={
                      takenPlacements[value] !== null && takenPlacements[value] !== managedDevice.id
                    }
                  >
                    {placementLabel(value)}
                  </option>
                ))}
              </select>

              <button
                onClick={() => disconnectDevice(managedDevice.id)}
                className="mt-2 w-full rounded-full border border-status-disconnected/40 px-3 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-status-disconnected transition duration-200 hover:border-status-disconnected/70 hover:bg-status-disconnected/10 active:scale-[0.98]"
              >
                Disconnect
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="font-mono text-xs text-muted">None Connected</p>
      )}

      {/* Reconnect indicator */}
      {status === 'reconnecting' && (
        <p className="rounded-2xl border border-status-scanning/30 bg-status-scanning/10 px-3 py-2 font-mono text-[11px] text-status-scanning">
          Attempt {reconnectAttempt} / 5…
        </p>
      )}

      {/* Error */}
      {error && (
        <p className="rounded-2xl border border-status-disconnected/30 bg-status-disconnected/10 px-3 py-2 font-mono text-[11px] text-status-disconnected">
          {error}
        </p>
      )}

      {/* Actions */}
      <div className="mt-auto flex flex-col gap-2">
        <button
          onClick={connect}
          disabled={isBusy || devices.length >= 2}
          className="pill-solid w-full"
        >
          {isBusy ? 'Connecting…' : devices.length >= 2 ? '2 Devices Connected' : 'Connect Device'}
        </button>

        {devices.length > 0 && (
          <button
            onClick={disconnectAll}
            className="w-full rounded-full border border-status-disconnected/50 px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-status-disconnected transition duration-200 hover:bg-status-disconnected hover:text-canvas active:scale-[0.98]"
          >
            Disconnect ALL
          </button>
        )}
      </div>
    </motion.aside>
  )
}
